// Main.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import { getCachedPublicMasjids, loadPublicMasjidPage } from "../services/publicMasjidCatalog";

import Body from "../components/Body";
import HomeHero from "../components/HomeHero";
import LatestTazkirah from "../components/LatestTazkirah";
import EtvSpotlight from "../components/EtvSpotlight";
import Masjid from "../components/Masjid";
import KitabStrip from "../components/KitabStrip";
import KitabView from "../components/KitabView";

import "../styles/Main.css";

const LS_KITABS = "v2_kitab_list_cache";

export default function Main() {
  const { masjidSlug } = useParams(); // /:masjidSlug (ikut routing kau)
  const [v2Masjids, setV2Masjids] = useState(() => getCachedPublicMasjids());
  const [kitabs, setKitabs] = useState([]);
  const [selectedKitab, setSelectedKitab] = useState(null);

  // Fetch the bounded public mosque page while preserving the production UI.
  useEffect(() => {
    let active = true;
    loadPublicMasjidPage()
      .then((page) => {
        if (active) setV2Masjids(page.items || []);
      })
      .catch((error) => console.error("❌ public mosque page error:", error));

    return () => { active = false; };
  }, []);

  // Fetch Kitab list
  useEffect(() => {
    const q = query(collection(dbAdmin, "kitabs"), orderBy("normalizedTitle", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setKitabs(rows);
        try {
          localStorage.setItem(LS_KITABS, JSON.stringify(rows));
        } catch { }
      },
      (err) => console.error("❌ load kitabs error:", err)
    );

    return () => unsub();
  }, []);

  // Filter ikut slug jika ada
  const filteredMasjids = useMemo(() => {
    if (!masjidSlug) return v2Masjids;
    const s = String(masjidSlug).toLowerCase();
    return v2Masjids.filter((m) => String(m.slug || "").toLowerCase() === s);
  }, [v2Masjids, masjidSlug]);

  // Lock body scroll bila KitabView dibuka
  useEffect(() => {
    document.body.classList.toggle("kitab-open", Boolean(selectedKitab));
    return () => document.body.classList.remove("kitab-open");
  }, [selectedKitab]);

  return (
    <div className="main-container">
      <section className="home-discovery-zone">
        <div className="main-home-discovery" aria-label="Pautan masjid dan kitab">
          <section className="home-discovery-strip" aria-label="Masjid dan surau">
            <Masjid v2Masjids={filteredMasjids} />
          </section>
          <section className="home-discovery-strip" aria-label="Kitab pengajian">
            <KitabStrip
              kitabs={kitabs}
              selectedKitabId={null}
              onSelect={(kitab) => setSelectedKitab(kitab)}
            />
          </section>
        </div>
      </section>

      {selectedKitab && (
        <>
          <button
            type="button"
            className="kitab-overlay"
            aria-label="Tutup Kitab"
            onClick={() => setSelectedKitab(null)}
          />
          <div className="main-section kitab-focus">
            <KitabView
              kitab={selectedKitab}
              v2Masjids={filteredMasjids}
              onClose={() => setSelectedKitab(null)}
            />
          </div>
        </>
      )}

      <section className="home-hero-zone">
        <HomeHero />
      </section>

      <section className="home-reading-zone" aria-label="Kandungan pilihan">
        <div className="main-home-section home-reading-grid">
          <LatestTazkirah />
          <EtvSpotlight v2Masjids={filteredMasjids} />
        </div>
      </section>

      <section className="home-kuliah-zone" aria-label="Kuliah terkini">
        <div className="main-section">
          <Body v2Masjids={filteredMasjids} />
        </div>
      </section>
    </div>
  );
}
